import React, { createContext, useState, useEffect, useContext } from "react";

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

// Available themes
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SUNSET: 'sunset',
  OCEAN: 'ocean',
  FOREST: 'forest',
  CANDY: 'candy',
  AURORA: 'aurora'
};

export const THEME_NAMES = {
  [THEMES.LIGHT]: 'Light',
  [THEMES.DARK]: 'Dark',
  [THEMES.SUNSET]: '🌅 Sunset Glow',
  [THEMES.OCEAN]: '🌊 Ocean Breeze',
  [THEMES.FOREST]: '🌲 Forest Mist',
  [THEMES.CANDY]: '🍭 Candy Pop',
  [THEMES.AURORA]: '🌌 Aurora Night'
};

function isNightTime() {
  const hour = new Date().getHours();
  // Dark mode from 6 PM (18:00) to 6 AM (6:00)
  return hour >= 18 || hour < 6;
}

export function ThemeProvider({ children }) {
  // Initialize theme state
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("selectedTheme");
    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      return savedTheme;
    }
    // Default to time-based (light/dark)
    return isNightTime() ? THEMES.DARK : THEMES.LIGHT;
  });

  // Initialize auto mode state
  const [isAutoMode, setIsAutoMode] = useState(() => {
    const saved = localStorage.getItem("themeAutoMode");
    return saved !== "false";
  });

  // Log current state
  useEffect(() => {
    console.log("Theme state - theme:", theme, "isAutoMode:", isAutoMode);
  }, [theme, isAutoMode]);

  useEffect(() => {
    // Remove all theme classes
    document.body.classList.remove(
      'light-theme', 
      'dark-theme', 
      'sunset-theme', 
      'ocean-theme', 
      'forest-theme', 
      'candy-theme', 
      'aurora-theme',
      'dark-mode' // legacy class
    );
    
    // Add current theme class
    document.body.classList.add(`${theme}-theme`);
    
    // Save to localStorage
    localStorage.setItem("selectedTheme", theme);
  }, [theme]);

  useEffect(() => {
    if (!isAutoMode) return;

    // Check time every minute and update theme if needed (only for light/dark)
    const interval = setInterval(() => {
      const shouldBeDark = isNightTime();
      const shouldBeTheme = shouldBeDark ? THEMES.DARK : THEMES.LIGHT;
      if (shouldBeTheme !== theme) {
        setTheme(shouldBeTheme);
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [theme, isAutoMode]);

  const changeTheme = (newTheme) => {
    // Manual theme change disables auto mode
    setIsAutoMode(false);
    localStorage.setItem("themeAutoMode", "false");
    setTheme(newTheme);
  };

  const enableAutoMode = () => {
    setIsAutoMode(true);
    localStorage.setItem("themeAutoMode", "true");
    
    // Immediately apply time-based theme
    setTheme(isNightTime() ? THEMES.DARK : THEMES.LIGHT);
  };

  const value = {
    theme,
    isAutoMode,
    changeTheme,
    enableAutoMode,
    // Backward compatibility
    isDarkMode: theme === THEMES.DARK,
    toggleTheme: () => changeTheme(theme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK)
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
